/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new VariantUpdateEvent(variant, sectionId, { html }));
 * document.addEventListener(ThemeEvents.variantUpdate, (e) => { console.log(e.detail.variant) });
 */
export class ThemeEvents {
  /** @static @constant {string} Event triggered when a variant is selected */
  static variantSelected = 'variant:selected';
  /** @static @constant {string} Event triggered when a variant is changed */
  static variantUpdate = 'variant:update';
  /** @static @constant {string} Event triggered when the cart items or quantities are updated */
  static cartUpdate = 'cart:update';
  /** @static @constant {string} Event triggered when a cart update fails */
  static cartError = 'cart:error';
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  static mediaStartedPlaying = 'media:started-playing';
  // Event triggered when quantity-selector value is changed
  static quantitySelectorUpdate = 'quantity-selector:update';
  /** @static @constant {string} Event triggered when a predictive search is expanded */
  static megaMenuHover = 'megaMenu:hover';
  /** @static @constant {string} Event triggered when a zoom dialog media is selected */
  static zoomMediaSelected = 'zoom-media:selected';
  /** @static @constant {string} Event triggered when a discount is applied */
  static discountUpdate = 'discount:update';
  /** @static @constant {string} Event triggered when a cart section re-renders after a back/forward cache restore */
  static cartSectionRestored = 'cart-section:restored';
  /** @static @constant {string} Event triggered when changing collection filters */
  static FilterUpdate = 'filter:update';
  /** @static @constant {string} Event triggered when the customizer store state changes */
  static customizerUpdate = 'customizer:update';
  /** @static @constant {string} Event triggered to request opening the customizer overlay */
  static customizerOpen = 'customizer:open';
  /** @static @constant {string} Event triggered when a control asks to confirm a removal */
  static customizerConfirmRemove = 'customizer:confirm-remove';
  /** @static @constant {string} Event triggered when the shopper confirms a removal in the dialog */
  static customizerRemoveConfirmed = 'customizer:remove-confirmed';
  /** @static @constant {string} Event triggered when the photo edit mode starts/ends */
  static customizerPhotoEdit = 'customizer:photo-edit';
  /** @static @constant {string} Event triggered to slide a customizer message in or take it down */
  static customizerToast = 'customizer:toast';
}

/**
 * Event fired when a variant is selected
 * @extends {Event}
 */
export class VariantSelectedEvent extends Event {
  /**
   * Creates a new VariantSelectedEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The option value id
   */
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * Event fired after a variant is updated
 * @extends {Event}
 */
export class VariantUpdateEvent extends Event {
  /**
   * Creates a new VariantUpdateEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   * @param {boolean} resource.available - Whether the variant is available
   * @param {boolean} resource.inventory_management - Whether the variant has inventory management
   * @param {string} [resource.sku] - The SKU of the variant
   * @param {Object} [resource.featured_media] - The featured media of the variant
   * @param {string} [resource.featured_media.id] - The id of the featured media
   * @param {Object} [resource.featured_media.preview_image] - The preview image of the featured media
   * @param {string} [resource.featured_media.preview_image.src] - The src URL of the preview image
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} data - Additional event data
   * @param {Document} data.html - The new document fragment for the variant
   * @param {string} data.productId - The product ID of the updated variant, used to ensure the correct product form is updated
   * @param {Object} [data.newProduct] - If a new product was loaded as part of the variant update (combined listing)
   * @param {string} data.newProduct.id - The id of the new product
   * @param {string} data.newProduct.url - The url of the new product
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
        newProduct: data.newProduct,
      },
    };
  }
}

/**
 * Event class for cart additions
 * @extends {Event}
 */
export class CartAddEvent extends Event {
  /**
   * Creates a new CartAddEvent
   * @param {Object} [resource] - The new cart object. Missing after a product form add whose
   * response carries a cart section with the count: listeners read it from `data.sections`.
   * @param {string} [sourceId] - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was added
   * @param {string} [data.requestId] - Correlates a customizer submission with its response.
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   * @param {string} [data.lineKey] - The key of the cart line the add landed on
   */
  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }

  static eventName = ThemeEvents.cartUpdate;
}

/**
 * Event class for cart updates
 * @extends {Event}
 */
export class CartUpdateEvent extends Event {
  /**
   * Creates a new CartUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was updated
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }
}

/**
 * Event class for cart errors
 * @extends {Event}
 */
export class CartErrorEvent extends Event {
  /**
   * Creates a new CartErrorEvent
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {string} message - A message from the server response
   * @param {Object} description - Description from the server response
   * @param {Object} errors - Errors from the server response
   * @param {string} [requestId] - The customizer submission that failed.
   * @param {number} [status] - The HTTP status of a refusal that came back without a message.
   */
  constructor(sourceId, message, description, errors, requestId, status) {
    super(ThemeEvents.cartError, { bubbles: true });
    this.detail = {
      sourceId,
      requestId,
      data: {
        message,
        errors,
        description,
        status,
      },
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class QuantitySelectorUpdateEvent extends Event {
  /**
   * Creates a new QuantitySelectorUpdateEvent
   * @param {number} quantity - Quantity value
   * @param {number} [cartLine] - The id of the updated cart line
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = {
      quantity,
      cartLine,
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class DiscountUpdateEvent extends Event {
  /**
   * Creates a new DiscountUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   */
  constructor(resource, sourceId) {
    super(ThemeEvents.discountUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
    };
  }
}

/**
 * Event class for media playback starts
 * @extends {Event}
 */
export class MediaStartedPlayingEvent extends Event {
  /**
   * Creates a new MediaStartedPlayingEvent
   * @param {HTMLElement} resource - The element containing the video that emitted the event
   */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * @typedef {Object} SlideshowSelectEventData
 * @property {number} index
 * @property {string | null} id
 * @property {Element} slide
 * @property {number} previousIndex
 * @property {boolean} userInitiated
 * @property {'select' | 'scroll' | 'drag'} trigger
 */

export class SlideshowSelectEvent extends Event {
  /**  @param {SlideshowSelectEventData} data */
  constructor(data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true });
    this.detail = data;
  }

  /** @type {SlideshowSelectEventData} */
  detail;

  static eventName = 'slideshow:select';
}

/**
 * Event class for zoom dialog media selection
 * @extends {Event}
 */
export class ZoomMediaSelectedEvent extends Event {
  /**
   * Creates a new ZoomMediaSelectedEvent
   * @param {number} index - The index of the selected media
   */
  constructor(index) {
    super(ThemeEvents.zoomMediaSelected, { bubbles: true });
    this.detail = {
      index,
    };
  }
}

/**
 * Event class for mega menu hover being hovered over
 * @extends {Event}
 */
export class MegaMenuHoverEvent extends Event {
  constructor() {
    super(ThemeEvents.megaMenuHover, { bubbles: true });
  }
}

/** Event class for facet filtering updates */
export class FilterUpdateEvent extends Event {
  /** @param {URLSearchParams} queryParams */
  constructor(queryParams) {
    super(ThemeEvents.FilterUpdate, { bubbles: true });
    this.detail = {
      queryParams,
    };
  }

  shouldShowClearAll() {
    return (
      [...this.detail.queryParams.entries()].filter(([key]) => key.startsWith('filter.')).length > 0
    );
  }
}

/**
 * Event fired when the customizer store mutates (init, navigation, future input capture).
 *
 * Consumers (step components, price/cart) listen on `document` and re-read the store from the
 * `detail`. The detail is typed against the public store shape only (no vendor->custom import),
 * so this stays decoupled from the `CustomizerStore` implementation.
 *
 * @extends {Event}
 */
export class CustomizerUpdateEvent extends Event {
  /**
   * @param {{ currentStep: string | null, steps: Record<string, object>, isEmpty: boolean }} store - The customizer store that changed.
   * @param {string} configId - The config-id of the changed customizer instance (its DOM key).
   */
  constructor(store, configId) {
    super(ThemeEvents.customizerUpdate, { bubbles: true });
    this.detail = {
      configId,
      currentStep: store.currentStep,
      steps: store.steps,
      isEmpty: store.isEmpty,
      store,
    };
  }
}

/**
 * Event fired to request opening the customizer overlay for a given config.
 *
 * Dispatched by the PDP trigger block, which lives in a different section than the overlay, so it
 * travels the document event bus rather than a direct method call. The overlay listens on
 * `document` and opens only when `configId` matches its own `data-config-id`.
 *
 * @extends {Event}
 */
export class CustomizerOpenEvent extends Event {
  /**
   * @param {string} configId - The config-id of the customizer instance to open (its DOM key).
   */
  constructor(configId) {
    super(ThemeEvents.customizerOpen, { bubbles: true });
    this.detail = { configId };
  }
}

/**
 * Event fired when a control's "Remove" is clicked, asking the shared confirm dialog to open.
 * The dialog lives once per overlay; controls travel this on the document bus so any control on
 * any face can trigger the same "Are you sure?" modal.
 *
 * @extends {Event}
 */
export class CustomizerConfirmRemoveEvent extends Event {
  /**
   * @param {string} configId - The customizer instance whose control is being cleared.
   * @param {string} controlKey - The control key to clear on confirm.
   * @param {string | null} [body] - Optional dialog body copy override (e.g. the photo variant
   *   "This will remove your photo"); null keeps the dialog's default engraving copy.
   */
  constructor(configId, controlKey, body = null) {
    super(ThemeEvents.customizerConfirmRemove, { bubbles: true });
    this.detail = { configId, controlKey, body };
  }
}

/**
 * Event fired by the confirm dialog once the shopper presses "Yes, I'm sure". The owning control
 * listens for its own `controlKey` and clears both the store value and its inputs.
 *
 * @extends {Event}
 */
export class CustomizerRemoveConfirmedEvent extends Event {
  /**
   * @param {string} configId - The customizer instance whose control was cleared.
   * @param {string} controlKey - The confirmed control key to clear.
   */
  constructor(configId, controlKey) {
    super(ThemeEvents.customizerRemoveConfirmed, { bubbles: true });
    this.detail = { configId, controlKey };
  }
}

/**
 * Event fired when a cart section re-renders after a back/forward cache restore.
 *
 * Bubbles because `cart-drawer-component` listens on itself and the dispatcher is the
 * `cart-items-component` nested inside it.
 *
 * @extends {Event}
 */
export class CartSectionRestoredEvent extends Event {
  constructor() {
    super(ThemeEvents.cartSectionRestored, { bubbles: true });
  }
}

/**
 * Event fired by the engrave router when the Edit Photo view is entered or left. The hero
 * preview's `<custom-customizer-photo-editor>` listens: on `active` it replaces the rendered
 * preview with the interactive crop canvas for `controlKey`'s photo; on inactive it restores it.
 *
 * @extends {Event}
 */
export class CustomizerPhotoEditEvent extends Event {
  /**
   * @param {string} configId - The customizer instance whose photo is being edited.
   * @param {string} controlKey - The photo upload control the edit targets.
   * @param {boolean} active - True entering the edit view, false leaving it.
   */
  constructor(configId, controlKey, active) {
    super(ThemeEvents.customizerPhotoEdit, { bubbles: true });
    this.detail = { configId, controlKey, active };
  }
}

/**
 * Event fired to slide a message into the customizer's `<custom-customizer-toast>`, or take it
 * down. Only the `source` that raised a message can take it down.
 *
 * @extends {Event}
 */
export class CustomizerToastEvent extends Event {
  /**
   * @param {string} configId - The customizer instance the message is for.
   * @param {object} source - Who is speaking.
   * @param {{ title: string, body: string } | null} message - What to say, or null to take it down.
   */
  constructor(configId, source, message) {
    super(ThemeEvents.customizerToast);
    this.detail = { configId, source, message };
  }
}
